import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import DataTable, { Column } from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import { adminApi } from '../../services/admin';
import { toGGScore, getScoreColor, getScoreColorClass } from '../../utils/score';
import type { SiteListItem } from '../../types';

const columns: Column<SiteListItem>[] = [
  { key: 'name', header: 'Name' },
  { key: 'city', header: 'City' },
  { key: 'state', header: 'State' },
  { key: 'client_org_name', header: 'Client' },
  { key: 'vendor_org_name', header: 'Vendor', render: (r) => r.vendor_org_name || '—' },
  { key: 'score', header: 'Score', render: (r) => (
    <span className={`font-semibold ${getScoreColorClass(getScoreColor(r.score))}`}>
      {toGGScore(r.score)}
    </span>
  )},
  { key: 'vendor_assignment_status', header: 'Status', render: (r) => (
    <StatusBadge
      label={r.vendor_assignment_status}
      variant={r.vendor_assignment_status === 'active' ? 'success' : r.vendor_assignment_status === 'pending' ? 'warning' : 'error'}
    />
  )},
  { key: 'guard_count', header: 'Guards' },
];

export default function AdminSites() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'sites', page, search],
    queryFn: () => adminApi.getSites({ page: String(page), search: search || undefined }),
  });

  const totalPages = data ? Math.ceil(data.count / 20) : 1;

  return (
    <>
      <PageHeader title="Sites" description="All sites across clients" />
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search sites..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="w-full sm:w-80 rounded-xl border-2 border-slate-200 px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </div>
      <DataTable
        columns={columns}
        data={data?.results ?? []}
        keyExtractor={(r) => r.id}
        isLoading={isLoading}
        emptyTitle="No sites"
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        onRowClick={(r) => navigate(`/admin/sites/${r.id}`)}
      />
    </>
  );
}
